import { readStore, writeStore, type WorkflowsStore } from './workflowsStorage';
import type { WorkflowDefinition, Workflow, DrainingVersion } from '~/types/workflow.types';
import type {
    CreateWorkflowComment,
    CreateWorkflow,
    ListWorkflowsParams,
    UpdateDraft,
    UpdateWorkflow,
    UpdateWorkflowComment,
    WorkflowComment,
    WorkflowsClientInterface,
} from './workflowsClient.types';
import { AxiosError, AxiosHeaders, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

// The localStorage fake fabricates axios envelopes and axios-style errors so it is a
// drop-in for the real HTTP client: callers read response.data and branch on
// error.response?.status exactly as they will against the backend.
const fakeConfig = (): InternalAxiosRequestConfig => ({ headers: new AxiosHeaders() });

const ok = <T>(data: T, status = 200): AxiosResponse<T> => ({
    data,
    status,
    statusText: 'OK',
    headers: {},
    config: fakeConfig(),
});

const fail = (status: number, message: string, data?: unknown): AxiosError => {
    const config = fakeConfig();
    const response: AxiosResponse = {
        data: data ?? { message },
        status,
        statusText: message,
        headers: {},
        config,
    };

    return new AxiosError(message, String(status), config, undefined, response);
};

const generateId = (prefix: string): string => `${prefix}_${crypto.randomUUID()}`;

const latestVersion = (versions: Workflow[]): Workflow | undefined => {
    const sorted = [...versions].sort((a, b) => a.version - b.version);

    return sorted[sorted.length - 1];
};

// The version a browse list should surface: the live (latest published) version, or the
// latest draft for workflows never published. Keeps an in-progress draft from replacing
// the published row a contact would actually receive.
const displayVersion = (versions: Workflow[]): Workflow | undefined => {
    const published = versions.filter((workflow) => workflow.status === 'published');

    return latestVersion(published.length > 0 ? published : versions);
};

export const getWorkflowDependencies = (definition: WorkflowDefinition): string[] => {
    const ids = new Set<string>();

    for (const step of definition.steps) {
        if (
            step.type === 'send_to_workflow' &&
            typeof step.target_workflow_id === 'string' &&
            step.target_workflow_id !== ''
        ) {
            ids.add(step.target_workflow_id);
        }
    }

    return [...ids];
};

// Minimal publish-time validation; AUDI-205 will expand the rule set. Step-internal edges
// (e.g. condition branch targets) are validated by their step components, not here.
export const validateDefinition = (definition: WorkflowDefinition): string[] => {
    const issues: string[] = [];

    if (definition.name.trim() === '') {
        issues.push('Workflow name is required.');
    }

    if (definition.steps.length === 0) {
        issues.push('Workflow must contain at least one step.');
    }

    const stepIds = new Set(definition.steps.map((step) => step.step_id));
    for (const step of definition.steps) {
        if (step.next_step !== undefined && !stepIds.has(step.next_step)) {
            issues.push(`Step "${step.step_id}" points to unknown step "${step.next_step}".`);
        }
    }

    return issues;
};

// Returned objects are cloned so they never alias the store, matching a real HTTP client's
// fresh-deserialized semantics. (Methods are async only to honor the interface.)
export default class LocalStorageWorkflowsClient implements WorkflowsClientInterface {
    async getWorkflows(params?: ListWorkflowsParams): Promise<AxiosResponse<Workflow[]>> {
        const store = readStore();

        const archived = params?.archived ?? false;
        const query = params?.query ? params.query.toLocaleLowerCase() : undefined;

        return ok(
            this.displayRows(store).filter((workflow) => {
                const isArchived = archived === (workflow.deleted_at !== null);
                const matchesEntryTriggerType =
                    !params?.entry_trigger_type ||
                    workflow.definition.entry_trigger?.type === params.entry_trigger_type;
                const includesQuery = !query || workflow.definition.name.toLocaleLowerCase().includes(query);

                return isArchived && matchesEntryTriggerType && includesQuery;
            })
        );
    }

    async getWorkflow(workflowId: string, version?: number): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);

        if (version !== undefined) {
            // Explicit-version lookups intentionally return archived versions: history
            // and version-detail pages inspect them.
            const match = versions.find((workflow) => workflow.version === version);
            if (match === undefined) {
                throw fail(404, `Version ${version} of workflow "${workflowId}" was not found.`);
            }

            return ok({ ...structuredClone(match), ...this.versionPointers(versions) });
        }

        const latest = latestVersion(versions.filter((workflow) => workflow.deleted_at === null));
        if (latest === undefined) {
            throw fail(404, `Workflow "${workflowId}" was not found.`);
        }

        return ok({ ...structuredClone(latest), ...this.versionPointers(versions) });
    }

    async getDependentWorkflows(workflowId: string): Promise<AxiosResponse<Workflow[]>> {
        const store = readStore();

        const dependents = this.displayRows(store)
            .filter((workflow) => workflow.deleted_at === null)
            .filter((workflow) => workflow.workflow_id !== workflowId)
            .filter((workflow) => getWorkflowDependencies(workflow.definition).includes(workflowId));

        return ok(dependents);
    }

    async createWorkflow(input: CreateWorkflow): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const definition: WorkflowDefinition =
            input.definition !== undefined
                ? {
                      ...structuredClone(input.definition),
                      name: input.name,
                      description: input.description ?? input.definition.description ?? '',
                  }
                : { name: input.name, description: input.description ?? '', steps: [] };
        const workflow: Workflow = {
            workflow_id: generateId('workflow'),
            version: 1,
            status: 'draft',
            published_at: null,
            deleted_at: null,
            updated_at: new Date().toISOString(),
            x: input.x,
            y: input.y,
            definition,
        };

        store.workflows[workflow.workflow_id] = [workflow];
        this.save(store);

        return ok({ ...structuredClone(workflow), ...this.versionPointers([workflow]) }, 201);
    }

    async updateWorkflow(workflowId: string, input: UpdateWorkflow): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);

        // Workflow-level fields apply to every version row, like deleted_at on archive.
        for (const workflow of versions) {
            Object.assign(workflow, input);
        }
        this.save(store);

        const display = displayVersion(versions);
        if (display === undefined) {
            throw fail(404, `Workflow "${workflowId}" was not found.`);
        }

        return ok({ ...structuredClone(display), ...this.versionPointers(versions) });
    }

    async createDraft(workflowId: string): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);
        const latest = latestVersion(versions);

        if (latest === undefined || latest.deleted_at !== null) {
            throw fail(409, `Workflow "${workflowId}" is archived.`);
        }

        // A draft is already open — editing it directly is the right behavior, so this is
        // idempotent rather than an error.
        if (latest.status === 'draft') {
            return ok(structuredClone(latest));
        }

        const draft: Workflow = {
            workflow_id: workflowId,
            version: latest.version + 1,
            status: 'draft',
            published_at: null,
            deleted_at: null,
            updated_at: new Date().toISOString(),
            x: latest.x,
            y: latest.y,
            definition: structuredClone(latest.definition),
        };

        versions.push(draft);
        this.save(store);

        return ok(structuredClone(draft), 201);
    }

    async updateDraft(workflowId: string, input: UpdateDraft): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const draft = this.requireEditableDraft(store, workflowId);

        draft.definition = structuredClone(input.definition);
        draft.updated_at = new Date().toISOString();
        this.save(store);

        const versions = this.requireVersions(store, workflowId);
        return ok({ ...structuredClone(draft), ...this.versionPointers(versions) });
    }

    async publishWorkflow(workflowId: string): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const draft = this.requireEditableDraft(store, workflowId);

        const issues = validateDefinition(draft.definition);
        if (issues.length > 0) {
            throw fail(422, 'Workflow definition is invalid.', {
                message: 'Workflow definition is invalid.',
                errors: issues,
            });
        }

        const now = new Date().toISOString();
        draft.status = 'published';
        draft.published_at = now;
        draft.updated_at = now;
        this.save(store);

        return ok(structuredClone(draft));
    }

    async archiveWorkflow(workflowId: string): Promise<AxiosResponse<void>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);
        const now = new Date().toISOString();

        for (const workflow of versions) {
            workflow.deleted_at = now;
        }
        this.save(store);

        return ok<void>(undefined);
    }

    async restoreWorkflow(workflowId: string): Promise<AxiosResponse<void>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);

        for (const workflow of versions) {
            workflow.deleted_at = null;
        }
        this.save(store);

        return ok<void>(undefined);
    }

    async getWorkflowVersions(workflowId: string): Promise<AxiosResponse<Workflow[]>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);

        return ok([...versions].sort((a, b) => b.version - a.version).map((workflow) => structuredClone(workflow)));
    }

    async getDrainingVersions(workflowId: string): Promise<AxiosResponse<DrainingVersion[]>> {
        const store = readStore();

        return ok(structuredClone(store.drainingVersions[workflowId] ?? []));
    }

    private versionPointers(versions: Workflow[]): Pick<Workflow, 'published_version' | 'draft_version'> {
        const published = versions.filter((workflow) => workflow.status === 'published');
        return {
            published_version: published.length > 0 ? Math.max(...published.map((workflow) => workflow.version)) : null,
            draft_version: versions.find((workflow) => workflow.status === 'draft')?.version ?? null,
        };
    }

    async getWorkflowComments(): Promise<AxiosResponse<WorkflowComment[]>> {
        const store = readStore();

        return ok(structuredClone(store.comments));
    }

    async createWorkflowComment(input: CreateWorkflowComment): Promise<AxiosResponse<WorkflowComment>> {
        const store = readStore();
        const comment: WorkflowComment = {
            comment_id: generateId('comment'),
            x: input.x,
            y: input.y,
            width: input.width,
            height: input.height,
            content: input.content ?? '',
        };

        store.comments.push(comment);
        this.save(store);

        return ok(structuredClone(comment), 201);
    }

    async updateWorkflowComment(
        commentId: string,
        input: UpdateWorkflowComment
    ): Promise<AxiosResponse<WorkflowComment>> {
        const store = readStore();
        const comment = store.comments.find((candidate) => candidate.comment_id === commentId);
        if (comment === undefined) {
            throw fail(404, `Comment "${commentId}" was not found.`);
        }

        Object.assign(comment, input);
        this.save(store);

        return ok(structuredClone(comment));
    }

    async deleteWorkflowComment(commentId: string): Promise<AxiosResponse<void>> {
        const store = readStore();
        const remaining = store.comments.filter((comment) => comment.comment_id !== commentId);
        if (remaining.length === store.comments.length) {
            throw fail(404, `Comment "${commentId}" was not found.`);
        }

        store.comments = remaining;
        this.save(store);

        return ok<void>(undefined);
    }

    // One row per workflow: the display version (live published, else latest draft) plus the
    // parent's denormalized published/draft version pointers the list surfaces for badges.
    private displayRows(store: WorkflowsStore): Workflow[] {
        return Object.values(store.workflows)
            .map((versions) => {
                const display = displayVersion(versions);
                return display === undefined
                    ? undefined
                    : { ...structuredClone(display), ...this.versionPointers(versions) };
            })
            .filter((workflow): workflow is Workflow => workflow !== undefined);
    }

    private requireVersions(store: WorkflowsStore, workflowId: string): Workflow[] {
        const versions = store.workflows[workflowId];
        if (versions === undefined || versions.length === 0) {
            throw fail(404, `Workflow "${workflowId}" was not found.`);
        }

        return versions;
    }

    private requireEditableDraft(store: WorkflowsStore, workflowId: string): Workflow {
        const versions = this.requireVersions(store, workflowId);
        const latest = latestVersion(versions);

        if (latest === undefined || latest.status !== 'draft') {
            throw fail(409, `Workflow "${workflowId}" has no open draft; create one before editing.`);
        }

        if (latest.deleted_at !== null) {
            throw fail(409, `Workflow "${workflowId}" is archived.`);
        }

        return latest;
    }

    private save(store: WorkflowsStore): void {
        try {
            writeStore(store);
        } catch {
            // Every call re-reads from localStorage, so a failed write silently loses the
            // mutation on the next operation — surface it as a 500 so the UI can warn.
            throw fail(500, 'The change could not be saved.');
        }
    }
}
